/**
 * Branch Management Hooks
 * Custom hooks for comprehensive branch CRUD operations using reusable fetch hooks
 */

import { useCallback, useState } from "react";
import { toast } from "sonner";
import { API_ENDPOINTS } from "../../../config/api-endpoints";
import { useDelete, useGet, usePost } from "../../../packages/hooks";
import { buildApiUrl } from "../../../packages/hooks/hook-utils";
import { selectAuthToken, useAuthStore } from "../../../store/useAuth";
import { useServerPagination } from "../../../hooks/use-server-pagination";
import {
  Branch,
  BranchListResponse,
  BranchStatistics,
} from "../types/branch.types";
import {
  handleHttpError,
  extractErrorMessage,
} from "../../../utils/error-utils";

/**
 * Branches hook return type
 */
export interface UseBranchesReturn {
  branches: Branch[];
  isLoading: boolean;
  totalItems: number;
  fetchBranches: () => Promise<void>;
  handleCreateBranch: (values: Partial<Branch>) => Promise<boolean>;
  handleUpdateBranch: (id: string, values: Partial<Branch>) => Promise<boolean>;
  handleDeleteBranch: (id: string) => Promise<boolean>;
  handleToggleActive: (id: string) => Promise<boolean>;
  handleSetMain: (id: string) => Promise<boolean>;
  fetchBranchStatistics: (id: string) => Promise<BranchStatistics | null>;
  // Pagination properties
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (itemsPerPage: number) => void;
}

/**
 * Main hook for branch management
 * Provides all CRUD operations and statistics
 * @param selectedBranchId - Optional selected branch ID for filtering
 * @returns Branch state and management functions
 */
export const useBranches = (
  selectedBranchId?: string | null
): UseBranchesReturn => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  // Get token from auth store
  const token = useAuthStore(selectAuthToken);

  // Initialize server-side pagination
  const pagination = useServerPagination(totalItems);

  // Memoize callbacks to prevent infinite loops
  const handleBranchesSuccess = useCallback((data: BranchListResponse) => {
    // Handle multiple possible response structures
    let branchList: Branch[] = [];
    let totalCount = 0;

    if (data) {
      // Check for branches array directly
      if (Array.isArray(data?.branches)) {
        branchList = data.branches;
        totalCount = data?.pagination?.total || branchList.length;
      }
      // Check for data.branches structure
      else if (Array.isArray((data as any)?.data?.branches)) {
        branchList = (data as any).data.branches;
        totalCount =
          (data as any)?.data?.pagination?.total || branchList.length;
      }
      // Check if data itself is an array
      else if (Array.isArray(data)) {
        branchList = data as unknown as Branch[];
        totalCount = branchList.length;
      }
      // Check for data.data array structure
      else if (Array.isArray((data as any)?.data)) {
        branchList = (data as any).data;
        totalCount = branchList.length;
      }
    }

    setBranches(branchList);
    setTotalItems(totalCount);
  }, []);

  const handleBranchesError = useCallback(() => {
    // Error handled by the hook
    setBranches([]);
    setTotalItems(0);
  }, []);

  // Build query params with optional branch filter
  const queryParams = {
    ...pagination.getQueryParams,
    ...(selectedBranchId && { branchId: selectedBranchId }),
  };

  // Initialize GET query for branches list
  const branchesQuery = useGet<BranchListResponse>(
    API_ENDPOINTS.BRANCHES.LIST,
    {
      queryParams,
      onSuccess: handleBranchesSuccess,
      onError: handleBranchesError,
    }
  );

  // Create mutation
  const createMutation = usePost<Branch>(API_ENDPOINTS.BRANCHES.LIST, {
    onSuccess: () => {
      branchesQuery.refetch();
    },
    onError: () => {
      // Error handled by the hook
    },
  });

  // Delete mutation
  const deleteMutation = useDelete<Branch>(API_ENDPOINTS.BRANCHES.LIST, {
    onSuccess: () => {
      branchesQuery.refetch();
    },
    onError: () => {
      // Error handled by the hook
    },
  });

  /**
   * Fetch branches list
   */
  const fetchBranches = async (): Promise<void> => {
    await branchesQuery.refetch();
  };

  // Note: No manual refetch needed here - useGet automatically refetches when queryParams change
  // The queryKey in useGet includes queryParams, so React Query handles this automatically

  // TODO: Improve error handling and add proper response types

  /**
   * Create a new branch
   */
  const handleCreateBranch = async (values: Partial<Branch>): Promise<any> => {
    try {
      const response = await createMutation.mutateAsync(values);
      return response;
    } catch (error) {
      return error;
    }
  };

  // TODO: Improve error handling and add proper response types

  /**
   * Update an existing branch
   */
  const handleUpdateBranch = async (
    id: string,
    values: Partial<Branch>
  ): Promise<any> => {
    try {
      // Validate the ID
      if (!id || typeof id !== "string") {
        throw new Error("Invalid branch ID");
      }

      // Validate the token
      if (!token) {
        throw new Error("Authentication required");
      }

      // Construct the endpoint with ID
      const endpoint = `crm/branches/${id}`;
      const url = buildApiUrl(endpoint);

      if (!url) {
        throw new Error("URL is not available");
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          id,
          ...values,
        }),
      });

      if (!response.ok) {
        await handleHttpError(response);
      }

      await branchesQuery.refetch();

      return response;
    } catch (error) {
      console.error("Error updating branch:", error);
      return error;
    }
  };

  /**
   * Delete a branch
   */
  const handleDeleteBranch = async (id: string): Promise<boolean> => {
    try {
      // Validate the token
      if (!token) {
        throw new Error("Authentication required");
      }

      await deleteMutation.mutateAsync(id);
      toast.success("Филиал успешно удален");
      return true;
    } catch (error) {
      console.error("Error deleting branch:", error);
      toast.error(extractErrorMessage(error, "Ошибка при удалении филиала"));
      return false;
    }
  };

  /**
   * Toggle branch active status
   */
  const handleToggleActive = async (id: string): Promise<boolean> => {
    try {
      if (!token) {
        throw new Error("Authentication required");
      }

      const endpoint = `crm/branches/${id}/toggle-active`;
      const url = buildApiUrl(endpoint);

      if (!url) {
        throw new Error("URL is not available");
      }

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        await handleHttpError(response);
      }

      await branchesQuery.refetch();
      toast.success("Статус филиала успешно изменен");
      return true;
    } catch (error) {
      console.error("Error toggling branch active status:", error);
      toast.error(
        extractErrorMessage(error, "Ошибка при изменении статуса филиала")
      );
      return false;
    }
  };

  /**
   * Set branch as main branch
   */
  const handleSetMain = async (id: string): Promise<boolean> => {
    try {
      if (!token) {
        throw new Error("Authentication required");
      }

      const endpoint = `crm/branches/${id}/set-main`;
      const url = buildApiUrl(endpoint);

      if (!url) {
        throw new Error("URL is not available");
      }

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        await handleHttpError(response);
      }

      await branchesQuery.refetch();
      toast.success("Филиал успешно установлен как главный");
      return true;
    } catch (error) {
      console.error("Error setting main branch:", error);
      toast.error(
        extractErrorMessage(error, "Ошибка при установке главного филиала")
      );
      return false;
    }
  };

  /**
   * Fetch branch statistics
   */
  const fetchBranchStatistics = async (
    id: string
  ): Promise<BranchStatistics | null> => {
    try {
      if (!token) {
        throw new Error("Authentication required");
      }

      const endpoint = `crm/branches/${id}/statistics`;
      const url = buildApiUrl(endpoint);

      if (!url) {
        throw new Error("URL is not available");
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error("Error fetching branch statistics:", error);
      return null;
    }
  };

  return {
    branches,
    isLoading:
      branchesQuery.isLoading ||
      branchesQuery.isFetching ||
      createMutation.isPending ||
      deleteMutation.isPending,
    totalItems,
    fetchBranches,
    handleCreateBranch,
    handleUpdateBranch,
    handleDeleteBranch,
    handleToggleActive,
    handleSetMain,
    fetchBranchStatistics,
    // Pagination handlers
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    totalPages: pagination.totalPages,
    handlePageChange: pagination.handlePageChange,
    handleItemsPerPageChange: pagination.handleItemsPerPageChange,
  };
};
